import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';

/**
 * 確認ダイアログのProps
 * @description シフト希望の保存・削除前に確認を求めるダイアログ
 */
interface ConfirmDialogProps {
  /** ダイアログ表示フラグ */
  open: boolean;
  /** ダイアログタイトル */
  title: string;
  /** 確認メッセージ */
  message: string;
  /** 確認ボタンテキスト（例: "OK", "削除"） */
  confirmText: string;
  /** キャンセルボタンテキスト（例: "キャンセル"） */
  cancelText: string;
  /** 確認ボタンクリックハンドラー */
  onConfirm: () => void;
  /** キャンセルボタンクリックハンドラー（Escキーも対応） */
  onCancel: () => void;
  /** ローディング中フラグ（ボタン無効化用） */
  isLoading?: boolean;
}

/**
 * 確認ダイアログコンポーネント
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onCancel();
      }
      if (event.key === 'Enter' && !isLoading) {
        onConfirm();
      }
    },
    [onCancel, onConfirm, isLoading]
  );

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      onKeyDown={handleKeyDown}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          color="inherit"
          aria-label={cancelText}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          color="primary"
          variant="contained"
          autoFocus
          aria-label={confirmText}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isLoading ? '処理中...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
